import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApolloModule, APOLLO_OPTIONS } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { ApolloLink, InMemoryCache } from '@apollo/client/core';
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { environment } from '../environments/environment';
import { onError } from '@apollo/client/link/error';
import { extractFiles } from "extract-files";
import { frontendLogger, getFrontendContextAttributes, getOrCreateClientId } from './instrument';
import { trace } from '@opentelemetry/api';

@NgModule({ imports: [BrowserModule, ApolloModule], providers: [
        {
            provide: APOLLO_OPTIONS,
            useFactory: (httpLink: HttpLink) => {
                const http = httpLink.create({ uri: environment.graphqlUrl, extractFiles, withCredentials: true });

                const setClientHeaders = new ApolloLink((operation, forward) => {
                    const prev = operation.getContext()?.headers ?? {};
                    const clientId = getOrCreateClientId();

                    operation.setContext({
                        headers: {
                            ...prev,
                            'x-client-id': clientId,
                        },
                    });

                    const span = trace.getActiveSpan();
                    span?.setAttribute('client.id', clientId);
                    if (operation.operationName) span?.setAttribute('graphql.operation.name', operation.operationName);

                    return forward(operation);
                });

                const error = onError(({ networkError, graphQLErrors, operation, response }) => {
                    const attrs = {
                        ...getFrontendContextAttributes(),
                        graphql_operation_name: operation.operationName,
                        graphql_operation_type: operation.query?.definitions?.[0]?.kind,
                        graphql_has_graphql_errors: graphQLErrors?.length ? true : false,
                        graphql_graphql_errors_count: graphQLErrors?.length ?? 0,
                        graphql_response_has_data: Boolean((response as any)?.data),
                    };

                    if (graphQLErrors?.length) {
                        const simpleErrors = graphQLErrors.slice(0, 5).map((e) => ({
                            message: e.message,
                            path: Array.isArray(e.path) ? e.path.map((p) => String(p)) : undefined,
                            code: (e.extensions as any)?.code,
                        }));

                        frontendLogger.error('graphql_graphql_error', {
                            ...attrs,
                            graphql_error_messages: simpleErrors.map((e) => e.message).join(' | '),
                            graphql_errors_json: JSON.stringify(simpleErrors).slice(0, 2000),
                        });
                    }

                    if (networkError) {
                        const ne: any = networkError as any;
                        const msg = String(ne?.message ?? ne);
                        const looksLikeHttp2ProtocolError = msg.includes('ERR_HTTP2_PROTOCOL_ERROR');

                        frontendLogger.error('graphql_network_error', {
                            ...attrs,
                            network_error_name: ne?.name,
                            network_error_message: msg,
                            network_error_status_code: ne?.statusCode,
                            network_error_result: typeof ne?.result === 'string' ? ne.result.slice(0, 1000) : undefined,
                            network_error_http2_protocol: looksLikeHttp2ProtocolError,
                        });

                        const span = trace.getActiveSpan();
                        span?.setAttribute('graphql.network_error', true);
                        span?.setAttribute('graphql.network_error.message', msg);
                        if (looksLikeHttp2ProtocolError) span?.setAttribute('graphql.network_error.http2_protocol', true);
                    }
                });

                const link = ApolloLink.from([setClientHeaders, error, http]);
                return {
                    cache: new InMemoryCache(),
                    link,
                    defaultOptions: {
                        watchQuery: {
                            fetchPolicy: 'no-cache'
                        },
                        query: {
                            fetchPolicy: 'no-cache'
                        }
                    }
                };
            },
            deps: [HttpLink],
        },
        provideHttpClient(withInterceptorsFromDi()),
    ] })
export class graphqlModule { }